import { BusMessage, calcStringByteLength, MessageFactory, UID_BYTES_PER_ELEMENT, UID_SAFE_BYTES_PER_ELEMENT } from "flavr-common";
// Still relevant for Scripting
/**
 * Announces that media streaming started on a model.
 */
export class ModelMediaStreamStartedMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcStringByteLength(this.clientId);
    }
    constructor(uid, clientId) {
        super();
        this.uid = uid;
        this.clientId = clientId;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.clientId = stream.utf16String(this.clientId);
    }
    static is(msg) {
        return msg.type === ModelMediaStreamStartedMessage.typestatic;
    }
}
ModelMediaStreamStartedMessage.typestatic = "ModelMediaStreamStartedMessage";
ModelMediaStreamStartedMessage.subjectstatic = "general";
MessageFactory.register(ModelMediaStreamStartedMessage);
// Still relevant for Scripting
/**
 * Announces that media streaming ended on a model.
 */
export class ModelMediaStreamEndedMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === ModelMediaStreamEndedMessage.typestatic;
    }
}
ModelMediaStreamEndedMessage.typestatic = "ModelMediaStreamEndedMessage";
ModelMediaStreamEndedMessage.subjectstatic = "general";
MessageFactory.register(ModelMediaStreamEndedMessage);
// Still relevant for Scripting
/**
 * Announces that a model media video was resized.
 */
export class ModelMediaVideoResizedMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Uint32Array.BYTES_PER_ELEMENT * 2;
    }
    constructor(uid, width, height) {
        super();
        this.uid = uid;
        this.width = width;
        this.height = height;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.width = stream.uint32(this.width);
        this.height = stream.uint32(this.height);
    }
    static is(msg) {
        return msg.type === ModelMediaVideoResizedMessage.typestatic;
    }
}
ModelMediaVideoResizedMessage.typestatic = "ModelMediaVideoResizedMessage";
ModelMediaVideoResizedMessage.subjectstatic = "general";
MessageFactory.register(ModelMediaVideoResizedMessage);
// Still relevant for Scripting
/**
 * Announces that a model media video became invalid.
 */
export class ModelMediaVideoInvalidMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Uint32Array.BYTES_PER_ELEMENT; // errorCode
    }
    constructor(uid, errorCode) {
        super();
        this.uid = uid;
        this.errorCode = errorCode;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.errorCode = stream.uint32(this.errorCode);
    }
    static is(msg) {
        return msg.type === ModelMediaVideoInvalidMessage.typestatic;
    }
}
ModelMediaVideoInvalidMessage.typestatic = "ModelMediaVideoInvalidMessage";
ModelMediaVideoInvalidMessage.subjectstatic = "general";
MessageFactory.register(ModelMediaVideoInvalidMessage);
/**
 * Announces that transmission of a media stream started.
 */
export class MediaStreamTransmissionStartedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.streamId) + calcStringByteLength(this.streamType) + UID_SAFE_BYTES_PER_ELEMENT;
    }
    constructor(streamId, streamType, surfaceUid) {
        super();
        this.streamId = streamId;
        this.streamType = streamType;
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.streamId = stream.utf16String(this.streamId);
        this.streamType = stream.utf16String(this.streamType);
        this.surfaceUid = stream.uidSafe(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === MediaStreamTransmissionStartedMessage.typestatic;
    }
}
MediaStreamTransmissionStartedMessage.typestatic = "MediaStreamTransmissionStartedMessage";
MediaStreamTransmissionStartedMessage.subjectstatic = "peer";
MessageFactory.register(MediaStreamTransmissionStartedMessage);
/**
 * Requests starting a screen stream.
 */
export class RequestStartScreenStreamMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(surfaceUid) {
        super();
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.surfaceUid = stream.uid(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === RequestStartScreenStreamMessage.typestatic;
    }
}
RequestStartScreenStreamMessage.typestatic = "RequestStartScreenStreamMessage";
RequestStartScreenStreamMessage.subjectstatic = "general";
MessageFactory.register(RequestStartScreenStreamMessage);
/**
 * Requests stopping a screen stream.
 */
export class RequestStopScreenStreamMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(surfaceUid) {
        super();
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.surfaceUid = stream.uid(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === RequestStopScreenStreamMessage.typestatic;
    }
}
RequestStopScreenStreamMessage.typestatic = "RequestStopScreenStreamMessage";
RequestStopScreenStreamMessage.subjectstatic = "general";
MessageFactory.register(RequestStopScreenStreamMessage);
/**
 * Requests starting 3D player-view sharing.
 */
export class RequestStartPlayer3DViewSharingMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(surfaceUid) {
        super();
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.surfaceUid = stream.uid(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === RequestStartPlayer3DViewSharingMessage.typestatic;
    }
}
RequestStartPlayer3DViewSharingMessage.typestatic = "RequestStartPlayer3DViewSharingMessage";
RequestStartPlayer3DViewSharingMessage.subjectstatic = "general";
MessageFactory.register(RequestStartPlayer3DViewSharingMessage);
/**
 * Requests stopping 3D player-view sharing.
 */
export class RequestStopPlayer3DViewSharingMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(surfaceUid) {
        super();
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.surfaceUid = stream.uid(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === RequestStopPlayer3DViewSharingMessage.typestatic;
    }
}
RequestStopPlayer3DViewSharingMessage.typestatic = "RequestStopPlayer3DViewSharingMessage";
RequestStopPlayer3DViewSharingMessage.subjectstatic = "general";
MessageFactory.register(RequestStopPlayer3DViewSharingMessage);
/**
 * Requests entering fullscreen mode for media content.
 */
export class RequestFullscreenMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(surfaceUid) {
        super();
        this.surfaceUid = surfaceUid;
    }
    serialization(stream) {
        this.surfaceUid = stream.uid(this.surfaceUid);
    }
    static is(msg) {
        return msg.type === RequestFullscreenMessage.typestatic;
    }
}
RequestFullscreenMessage.typestatic = "RequestFullscreenMessage";
RequestFullscreenMessage.subjectstatic = "general";
MessageFactory.register(RequestFullscreenMessage);
